import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { getUser } from "../../../services/operations/authAPI";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  BookOpen,
  GraduationCap,
  Crown,
  Building2,
  ArrowRight,
  TrendingDown,
  X,
  Copy,
  ArrowLeft,
} from "lucide-react";

const makeDirectPayment = async (
  amount,
  plan,
  days,
  currency,
  navigate,
  dispatch,
  setIsVerifying
) => {
  try {
    const { data } = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/user/order`,
      { amount, currency }
    );

    if (!data.success) {
      toast.error("Failed to create Razorpay order");
      return;
    }

    const {
      id: order_id,
      amount: order_amount,
      currency: response_currency,
    } = data.data;

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: order_amount,
      currency: response_currency || currency,
      name: "MNR PSTVue",
      description: `${plan.name} Plan Subscription`,
      order_id: order_id,
      handler: async function (response) {
        setIsVerifying(true);

        try {
          const tokenString = localStorage.getItem("token");
          const token = tokenString ? JSON.parse(tokenString) : null;

          const verifyRes = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/user/verify`,
            {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan: plan.name?.toLowerCase(),
              days: days,
              amount: amount,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (verifyRes.data.success) {
            toast.success("Payment verified successfully!");
            await dispatch(getUser());
            navigate("/dashboard/student");
          } else {
            toast.error("Payment verification failed!");
            navigate("/dashboard/student");
          }
        } catch (error) {
          console.error("Verification error:", error);
          toast.error("An error occurred during verification.");
          navigate("/dashboard/student");
        } finally {
          setIsVerifying(false);
        }
      },
      modal: {
        ondismiss: function () {
          setIsVerifying(false);
          toast.error("Payment cancelled by user.");
        },
      },
      theme: {
        color: "#6366f1",
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  } catch (error) {
    console.error(error);
    toast.error("Something went wrong while processing payment.");
  }
};

export default function UpgradePlan() {
  const [isAnnual, setIsAnnual] = useState(false);
  const [currency, setCurrency] = useState("INR");
  const [showModal, setShowModal] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Dynamically load Razorpay script
  useEffect(() => {
    const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      });
    };
    loadRazorpayScript();
  }, []);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText("sales@mnrtechnologies.com");
    toast.success("Email copied to clipboard!");
  };

  const plans = [
    {
      name: "Free",
      icon: <BookOpen size={24} />,
      monthlyPrice: { INR: 0, USD: 0 },
      semiAnnualPrice: { INR: 0, USD: 0 },
      duration: "/ 14 Days",
      desc: "Experience our AI study assistant to solve your doubts completely free.",
      features: [
        "20 AI Chat Queries", // Updated from 10 to 20
        "14-Day Platform Access",
        "Text & PDF Document Uploads",
        "Step-by-step Explanations",
        "Standard Response Speed",
      ],
      highlighted: false,
    },
    {
      name: "Basic",
      icon: <GraduationCap size={24} />,
      monthlyPrice: { INR: 1000, USD: 12 },
      semiAnnualPrice: { INR: 5400, USD: 64.8 },
      duration: "/ month",
      desc: "Perfect for regular students needing consistent homework help and document analysis.",
      features: [
        // Updated to 500 per month (3,000 for 6 months)
        `${isAnnual ? "3,000" : "500"} AI Chat Queries`,
        "Advanced PDF Parsing",
        "Detailed Question Analysis",
        "Chat History Retention",
        "Faster Response Times",
      ],
      buttonText: "Get Basic",
      highlighted: false,
    },
    {
      name: "Premium",
      icon: <Crown size={24} />,
      monthlyPrice: { INR: 2000, USD: 24 },
      semiAnnualPrice: { INR: 10800, USD: 129.6 },
      duration: "/ month",
      desc: "Ultimate study toolkit for heavy users, detailed exam prep, and multi-document analysis.",
      features: [
        // Updated to 1,000 per month (6,000 for 6 months)
        `${isAnnual ? "6,000" : "1,000"} AI Chat Queries`,
        "Multi-PDF Upload Support",
        "In-depth Complex Problem Solving",
        "Priority Server Access",
        "Export Solutions to PDF",
      ],
      buttonText: "Upgrade to Premium",
      highlighted: true,
    },
    {
      name: "Institution",
      icon: <Building2 size={24} />,
      monthlyPrice: { INR: "Custom", USD: "Custom" },
      semiAnnualPrice: { INR: "Custom", USD: "Custom" },
      duration: "",
      desc: "Tailored solutions built for schools, colleges, and tutoring centers.",
      features: [
        "Unlimited Student Queries",
        "School-wide Custom Deployment",
        "Dedicated Server Infrastructure",
        "Admin Analytics Dashboard",
      ],
      buttonText: "Contact Sales",
      highlighted: false,
      isCustom: true,
    },
  ];

  const handleAction = (plan) => {
    if (plan.isCustom) {
      setShowModal(true);
      return;
    }

    const currentPrice = isAnnual
      ? plan.semiAnnualPrice[currency]
      : plan.monthlyPrice[currency];

    // If it's the free plan (0 price)
    if (currentPrice === 0) {
      toast.success("Free trial activated!");
      navigate("/dashboard/student");
      return;
    }

    const days = isAnnual ? 180 : 30;
    makeDirectPayment(
      currentPrice,
      plan,
      days,
      currency,
      navigate,
      dispatch,
      setIsVerifying
    );
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] bg-[#050505] text-white selection:bg-indigo-500/30">
      <div className="max-w-[90rem] mx-auto pb-12 pt-4 px-4 sm:px-6 relative overflow-hidden">
        {/* Background Glow Effect */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-indigo-600/10 blur-[120px] rounded-full pointer-events-none" />

        {/* BACK BUTTON */}
        <button
          onClick={() => navigate("/dashboard/student")}
          className="relative z-10 flex items-center gap-2 text-neutral-400 hover:text-white transition-colors font-medium w-fit mb-6"
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </button>

        {/* HEADER SECTION */}
        <div className="text-center max-w-2xl mx-auto relative z-10 mb-10">
          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight mb-4">
            Supercharge Your <span className="text-indigo-400">Study Space</span>
          </h1>
          <p className="text-neutral-400 text-lg leading-relaxed">
            Choose a plan that fits your study needs to get instant, accurate help with text or PDF-based questions.
          </p>

          {/* TOGGLES SECTION */}
          <div className="flex flex-col items-center justify-center gap-5 mt-6">
            <div className="flex bg-[#0A0A0A] border border-white/10 p-1 rounded-xl">
              <button
                onClick={() => setCurrency("INR")}
                className={`px-6 py-2 text-sm font-bold rounded-lg transition-all duration-200 ${
                  currency === "INR"
                    ? "bg-white/10 text-white shadow-sm"
                    : "text-neutral-500 hover:text-white"
                }`}
              >
                INR (₹)
              </button>
              <button
                onClick={() => setCurrency("USD")}
                className={`px-6 py-2 text-sm font-bold rounded-lg transition-all duration-200 ${
                  currency === "USD"
                    ? "bg-white/10 text-white shadow-sm"
                    : "text-neutral-500 hover:text-white"
                }`}
              >
                USD ($)
              </button>
            </div>

            <div className="flex items-center justify-center gap-4">
              <span
                className={`text-sm font-bold transition-colors ${
                  !isAnnual ? "text-white" : "text-neutral-500"
                }`}
              >
                Monthly
              </span>
              <button
                onClick={() => setIsAnnual(!isAnnual)}
                className="w-14 h-7 bg-white/10 border border-white/10 rounded-full p-1 relative transition-colors duration-200"
              >
                <div
                  className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${
                    isAnnual ? "translate-x-7" : "translate-x-0"
                  }`}
                ></div>
              </button>
              <div className="flex items-center gap-2">
                <span
                  className={`text-sm font-bold transition-colors ${
                    isAnnual ? "text-white" : "text-neutral-500"
                  }`}
                >
                  6 Months
                </span>
                <span className="bg-indigo-500/20 text-indigo-300 border border-indigo-500/20 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter">
                  Save ~10%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* PRICING CARDS GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch relative z-10">
          {plans.map((plan, idx) => {
            let displayPrice = "";
            let durationText = "";
            const currencySymbol = currency === "INR" ? "₹" : "$";

            if (plan.isCustom) {
              displayPrice = "Custom";
            } else if (plan.monthlyPrice[currency] === 0) {
              displayPrice = "Free";
              durationText = plan.duration;
            } else {
              const currentPrice = isAnnual
                ? plan.semiAnnualPrice[currency]
                : plan.monthlyPrice[currency];
              displayPrice = `${currencySymbol}${currentPrice.toLocaleString()}`;
              durationText = isAnnual ? "/ 6 mo" : plan.duration;
            }

            return (
              <div
                key={idx}
                className={`relative flex flex-col bg-[#0A0A0A] backdrop-blur-xl rounded-3xl p-6 transition-all duration-300 group overflow-hidden
                  ${
                    plan.highlighted
                      ? "border-2 border-indigo-500 shadow-2xl shadow-indigo-500/20 scale-100 lg:scale-105 z-10"
                      : "border border-white/10 shadow-sm hover:border-indigo-500/50"
                  }
                `}
              >
                <div
                  className={`absolute top-0 right-0 w-32 h-32 rounded-bl-full -z-10 transition-transform duration-500 group-hover:scale-150 blur-2xl
                  ${plan.highlighted ? "bg-indigo-500/20" : "bg-white/5"}
                `}
                ></div>

                {plan.highlighted && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-indigo-500 text-white px-4 py-1 rounded-b-lg text-[10px] font-black uppercase tracking-widest shadow-sm">
                    Most Popular
                  </div>
                )}

                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 shadow-sm border
                  ${
                    plan.highlighted
                      ? "bg-indigo-600 text-white border-indigo-500 mt-2"
                      : "bg-white/5 text-indigo-400 border-white/10"
                  }
                `}
                >
                  {plan.icon}
                </div>

                <h3 className="text-xl font-bold text-white mb-2">
                  {plan.name}
                </h3>
                <p className="text-sm text-neutral-400 leading-relaxed min-h-[40px] mb-6">
                  {plan.desc}
                </p>

                <div className="mb-6 min-h-[50px] flex flex-col justify-center">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-black text-white tracking-tight">
                      {displayPrice}
                    </span>
                    {durationText && (
                      <span className="text-neutral-500 text-xs font-medium">
                        {durationText}
                      </span>
                    )}
                  </div>
                  {isAnnual &&
                    !plan.isCustom &&
                    plan.monthlyPrice[currency] > 0 && (
                      <div className="text-[10px] text-indigo-400 font-bold mt-1 flex items-center gap-1">
                        <TrendingDown size={12} />
                        Total {currencySymbol}
                        {plan.semiAnnualPrice[currency].toLocaleString()} billed
                        half-yearly
                      </div>
                    )}
                </div>

                <hr className="border-white/10 mb-6" />

                <ul className="space-y-4 mb-8 flex-1">
                  {plan.features.map((feature, fIdx) => (
                    <li
                      key={fIdx}
                      className="flex items-start gap-3 text-sm text-neutral-300 font-medium leading-tight"
                    >
                      <CheckCircle2
                        size={16}
                        className="text-indigo-400 shrink-0 mt-0.5"
                      />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {plan.buttonText && (
                  <button
                    onClick={() => handleAction(plan)}
                    className={`w-full py-3 px-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all duration-200
                    ${
                      plan.highlighted
                        ? "bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-500/25"
                        : "bg-white/5 hover:bg-white/10 text-white border border-white/10"
                    }
                  `}
                  >
                    {plan.buttonText}
                    <ArrowRight
                      size={16}
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-center text-sm text-neutral-500 mt-8 relative z-10">
          Need a custom plan for your school or institution?{" "}
          <button
            onClick={() => setShowModal(true)}
            className="text-indigo-400 font-bold cursor-pointer hover:text-indigo-300 transition-colors"
          >
            Contact our Sales Team.
          </button>
        </p>

        {/* CONTACT/DEMO MODAL OVERLAY */}
        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
            <div className="bg-[#0A0A0A] border border-white/10 rounded-3xl p-8 w-full max-w-md text-center relative shadow-2xl">
              <button
                className="absolute top-4 right-4 text-neutral-500 hover:text-white hover:bg-white/10 p-2 rounded-xl transition-colors"
                onClick={() => setShowModal(false)}
              >
                <X size={20} />
              </button>

              <div className="w-16 h-16 bg-indigo-500/20 rounded-full flex items-center justify-center mx-auto mb-6 text-indigo-400 border border-indigo-500/30">
                <Building2 size={32} />
              </div>

              <h3 className="text-2xl font-black text-white mb-3 tracking-tight">
                Get in Touch
              </h3>
              <p className="text-neutral-400 text-sm leading-relaxed mb-6">
                To schedule a demonstration for school-wide deployment or discuss an institution-level plan, please reach out to our sales team directly.
              </p>

              <div className="bg-[#050505] border border-white/10 rounded-xl p-2 flex items-center justify-between mt-2">
                <div className="pl-4 pr-2 py-2 overflow-hidden">
                  <span className="text-neutral-300 font-bold text-sm md:text-base truncate block">
                    sales@mnrtechnologies.com
                  </span>
                </div>
                <button
                  onClick={handleCopyEmail}
                  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors shrink-0 shadow-md"
                >
                  <Copy size={16} />
                  Copy
                </button>
              </div>
            </div>
          </div>
        )}

        {/* VERIFICATION LOADING OVERLAY */}
        {isVerifying && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <div className="bg-[#0A0A0A] border border-white/10 p-8 rounded-3xl shadow-2xl flex flex-col items-center gap-5 max-w-sm w-full animate-in zoom-in-95 duration-200">
              <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-lg font-bold text-white">
                Payment is verifying...
              </p>
              <p className="text-sm text-neutral-400 text-center">
                Please do not close or refresh this window.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
